import { createReducer, on } from '@ngrx/store';
import { RegistrationStatus } from '@modules/companies/types/company';
import * as Actions from './shipping-agent-registration.actions';
import {
  handleClear,
  handleFailure,
  handleRequest,
  handleSuccess,
} from '@shared/ngrx-factory/factory.reducer';

const initialRequestState: Actions.RequestState<any> = {
  loading: false,
  message: null,
  response: null,
  error: null,
};

export const initialShippingAgentRegistrationState: Actions.ShippingAgentRegistrationState = {
  saveAgent: { ...initialRequestState },
  progress: { ...initialRequestState },
  summary: { ...initialRequestState },
  submitApplication: { ...initialRequestState },
  agentTypes: { ...initialRequestState },
};

export const shippingAgentRegistrationReducer = createReducer(
  initialShippingAgentRegistrationState,

  // Save Shipping Agent
  on(Actions.SaveShippingAgentActions.request, (state) =>
    handleRequest(state, 'saveAgent'),
  ),
  on(Actions.SaveShippingAgentActions.success, (state, { response }) =>
    handleSuccess(state, 'saveAgent', response),
  ),
  on(Actions.SaveShippingAgentActions.failure, (state, { error }) =>
    handleFailure(state, 'saveAgent', error),
  ),
  on(Actions.SaveShippingAgentActions.clearState, (state) =>
    handleClear(state, 'saveAgent'),
  ),

  // Agent Registration Progress
  on(Actions.AgentRegistrationProgressActions.request, (state) =>
    handleRequest(state, 'progress'),
  ),
  on(Actions.AgentRegistrationProgressActions.success, (state, { response }) =>
    handleSuccess(state, 'progress', response),
  ),
  on(Actions.AgentRegistrationProgressActions.failure, (state, { error }) =>
    handleFailure(state, 'progress', error),
  ),
  on(Actions.AgentRegistrationProgressActions.clearState, (state) =>
    handleClear(state, 'progress'),
  ),

  // Agent Registration Summary
  on(Actions.AgentRegistrationSummaryActions.request, (state) =>
    handleRequest(state, 'summary'),
  ),
  on(Actions.AgentRegistrationSummaryActions.success, (state, { response }) =>
    handleSuccess(state, 'summary', response),
  ),
  on(Actions.AgentRegistrationSummaryActions.failure, (state, { error }) =>
    handleFailure(state, 'summary', error),
  ),
  on(Actions.AgentRegistrationSummaryActions.clearState, (state) =>
    handleClear(state, 'summary'),
  ),

  // Submit Agent Application
  on(Actions.SubmitAgentApplicationActions.request, (state) =>
    handleRequest(state, 'submitApplication'),
  ),
  on(Actions.SubmitAgentApplicationActions.success, (state, { response }) => {
    const s = handleSuccess(state, 'submitApplication', response);

    const currentProgress = s.progress?.response?.data;
    const updatedProgress = currentProgress
      ? {
          ...s.progress,
          response: {
            ...s.progress.response,
            data: {
              ...currentProgress,
              applicationSubmitted: true,
              agentRegistration: currentProgress.agentRegistration
                ? {
                    ...currentProgress.agentRegistration,
                    registrationStatus: RegistrationStatus.APPLICATION_SUBMITTED,
                  }
                : currentProgress.agentRegistration,
            },
          },
        }
      : s.progress;

    const currentSummary = s.summary?.response?.data;
    const updatedSummary = currentSummary
      ? {
          ...s.summary,
          response: {
            ...s.summary.response,
            data: {
              ...currentSummary,
              status: RegistrationStatus.APPLICATION_SUBMITTED,
            },
          },
        }
      : s.summary;

    return {
      ...s,
      progress: updatedProgress,
      summary: updatedSummary,
    };
  }),
  on(Actions.SubmitAgentApplicationActions.failure, (state, { error }) =>
    handleFailure(state, 'submitApplication', error),
  ),
  on(Actions.SubmitAgentApplicationActions.clearState, (state) =>
    handleClear(state, 'submitApplication'),
  ),

  // Get Shipping Agent Types
  on(Actions.GetShippingAgentTypesActions.request, (state) =>
    handleRequest(state, 'agentTypes'),
  ),
  on(Actions.GetShippingAgentTypesActions.success, (state, { response }) =>
    handleSuccess(state, 'agentTypes', response),
  ),
  on(Actions.GetShippingAgentTypesActions.failure, (state, { error }) =>
    handleFailure(state, 'agentTypes', error),
  ),
  on(Actions.GetShippingAgentTypesActions.clearState, (state) =>
    handleClear(state, 'agentTypes'),
  ),

  // Clear All Context
  on(Actions.clearShippingAgentRegistrationContext, () => ({
    ...initialShippingAgentRegistrationState,
  })),
);
